import { publicPath, reference, sanitizeUtms, Utms, utmVocabulary, UtmKey } from './catalog';
import { getConsent } from './consent';
import { readStorage, writeStorage } from './storage';
export const ATTRIBUTION_KEY = 'antares_attribution_v1';
const RETENTION = 90 * 24 * 60 * 60 * 1000;
const SESSION = 30 * 60 * 1000;
export interface Touch { source: string; medium: string; utms: Utms; entry_path: string; referrer_domain: string; captured_at: number }
export interface Attribution { first: Touch; session: Touch; last_non_direct: Touch | null; last_activity: number }
function validTouch(raw: unknown, now: number): Touch | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Touch;
  const allowedSources = ['direct_unknown', 'other_referral', ...utmVocabulary.utm_source];
  const allowedMedium = ['none', ...utmVocabulary.utm_medium];
  if (!allowedSources.includes(t.source) || !allowedMedium.includes(t.medium as never) || !publicPath(t.entry_path || '') ||
    !Number.isFinite(t.captured_at) || t.captured_at > now || now - t.captured_at > RETENTION) return null;
  const ref = t.referrer_domain === 'other' ? 'other' : reference(`https://${t.referrer_domain}`).domain;
  const params = new URLSearchParams();
  for (const key of Object.keys(utmVocabulary) as UtmKey[]) if (typeof t.utms?.[key] === 'string') params.set(key, t.utms[key]!);
  return { source: t.source, medium: t.medium, utms: sanitizeUtms(params.toString()), entry_path: publicPath(t.entry_path)!, referrer_domain: ref, captured_at: t.captured_at };
}
export function captureAttribution(href: string, referrer: string, now = Date.now()): Attribution | null {
  if (getConsent() !== 'granted') return null;
  try {
    const url = new URL(href);
    const path = publicPath(url.pathname);
    if (!path) return null;
    const utms = sanitizeUtms(url.search);
    const ref = reference(referrer);
    const touch: Touch = { source: utms.utm_source || ref.source, medium: utms.utm_medium || (utms.utm_source ? 'none' : ref.medium),
      utms, entry_path: path, referrer_domain: ref.domain, captured_at: now };
    const saved = readStorage(ATTRIBUTION_KEY) as Partial<Attribution> | null;
    const first = validTouch(saved?.first, now) || touch;
    const previous = validTouch(saved?.session, now);
    const sameSession = previous && Number(saved?.last_activity) <= now && now - Number(saved?.last_activity) < SESSION;
    const session = sameSession ? previous : touch;
    const last = validTouch(saved?.last_non_direct, now);
    const result = { first, session, last_non_direct: session.source !== 'direct_unknown' ? session : last, last_activity: now };
    writeStorage(ATTRIBUTION_KEY, result);
    return result;
  } catch { return null; }
}
export function getAttribution(): Attribution | null {
  if (typeof window === 'undefined' || getConsent() !== 'granted') return null;
  return captureAttribution(window.location.href, document.referrer);
}
